// app/javascript/controllers/aircraft_autocomplete_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "results"]
    static values = {
        url: { type: String, default: "/faa_masters/search" },
        minChars: { type: Number, default: 2 },
        debounce: { type: Number, default: 250 }
    }

    connect() {
        console.log("🛩️ Aircraft autocomplete controller connected!")
        console.log("URL:", this.urlValue)
        console.log("Min chars:", this.minCharsValue)
        this.timeout = null
        this.selectedIndex = -1
    }

    search(event) {
        const query = this.inputTarget.value.trim()
        console.log("🔍 Search triggered! Query:", query, "Length:", query.length)

        // Clear timeout
        clearTimeout(this.timeout)

        // Check minimum characters (BR-007)
        if (query.length < this.minCharsValue) {
            console.log("⚠️ Query too short, need", this.minCharsValue, "characters")
            this.hideResults()
            return
        }

        console.log("⏱️ Setting timeout for", this.debounceValue, "ms")
        // Debounce the search
        this.timeout = setTimeout(() => {
            console.log("✅ Timeout complete, performing search for:", query)
            this.performSearch(query)
        }, this.debounceValue)
    }

    async performSearch(query) {
        console.log("🌐 Fetching from:", `${this.urlValue}?query=${query}`)

        try {
            this.showLoading()

            const response = await fetch(`${this.urlValue}?query=${encodeURIComponent(query)}`, {
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })

            console.log("📡 Response status:", response.status)

            if (!response.ok) throw new Error('Search failed')

            const data = await response.json()
            console.log("📦 Received data:", data)
            this.displayResults(data)
        } catch (error) {
            console.error('❌ Aircraft search error:', error)
            this.showError()
        }
    }

    displayResults(aircraft) {
        if (!aircraft || aircraft.length === 0) {
            this.showNoResults()
            return
        }

        this.resultsTarget.innerHTML = aircraft
            .slice(0, 10) // Max 10 suggestions
            .map((item, index) => this.resultItemHTML(item, index))
            .join("")

        this.showResults()
        this.selectedIndex = -1
    }

    resultItemHTML(aircraft, index) {
        const yearDisplay = aircraft.year ? `(${aircraft.year})` : ""

        return `
      <button
        type="button"
        data-action="click->aircraft-autocomplete#selectAircraft"
        data-n-number="${this.escapeHtml(aircraft.n_number)}"
        data-make-model="${this.escapeHtml(aircraft.make_model)}"
        data-year="${this.escapeHtml(aircraft.year || '')}"
        data-index="${index}"
        class="w-full px-4 py-3 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-200 last:border-b-0"
      >
        <div class="flex items-center justify-between">
          <div>
            <div class="font-semibold text-gray-900">${"N" + this.escapeHtml(aircraft.n_number)}</div>
            <div class="text-sm text-gray-600">${this.escapeHtml(aircraft.make_model)} ${yearDisplay}</div>
          </div>
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
        </div>
      </button>
    `
    }

    selectAircraft(event) {
        const button = event.currentTarget
        const aircraft = {
            nNumber: button.dataset.nNumber,
            makeModel: button.dataset.makeModel,
            year: button.dataset.year
        }

        // Dispatch event to aircraft-selection controller
        const selectionController = this.application.getControllerForElementAndIdentifier(
            document.querySelector('[data-controller~="aircraft-selection"]'),
            "aircraft-selection"
        )

        if (selectionController) {
            selectionController.addAircraft({ detail: aircraft })
        }

        // Clear search
        this.inputTarget.value = ""
        this.hideResults()
    }

    // Keyboard navigation
    navigate(event) {
        const items = this.resultsTarget.querySelectorAll('button')
        if (items.length === 0) return

        switch(event.key) {
            case 'ArrowDown':
                event.preventDefault()
                this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
                this.highlightItem(items)
                break
            case 'ArrowUp':
                event.preventDefault()
                this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
                this.highlightItem(items)
                break
            case 'Enter':
                event.preventDefault()
                if (this.selectedIndex >= 0) {
                    items[this.selectedIndex].click()
                }
                break
            case 'Escape':
                this.hideResults()
                break
        }
    }

    highlightItem(items) {
        items.forEach((item, index) => {
            if (index === this.selectedIndex) {
                item.classList.add('bg-gray-100')
                item.focus()
            } else {
                item.classList.remove('bg-gray-100')
            }
        })
    }

    showLoading() {
        this.resultsTarget.innerHTML = `
      <div class="px-4 py-3 text-center text-gray-500">
        <svg class="animate-spin h-5 w-5 mx-auto" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="mt-2 text-sm">Searching...</p>
      </div>
    `
        this.showResults()
    }

    showNoResults() {
        this.resultsTarget.innerHTML = `
      <div class="px-4 py-3 text-center text-gray-500">
        <p class="text-sm">No aircraft found</p>
      </div>
    `
        this.showResults()
    }

    showError() {
        this.resultsTarget.innerHTML = `
      <div class="px-4 py-3 text-center text-red-600">
        <p class="text-sm">Search temporarily unavailable</p>
      </div>
    `
        this.showResults()
    }

    showResults() {
        this.resultsTarget.classList.remove('hidden')
    }

    hideResults() {
        this.resultsTarget.classList.add('hidden')
        this.selectedIndex = -1
    }

    // Click outside to close
    disconnect() {
        clearTimeout(this.timeout)
    }

    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }
}